import React, { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { useLang } from "@/i18n";
import { HOME_MEDIA } from "@/data/homeMedia";
import images from "@/data/homeImageManifest.json";
import { Pause, Play } from "lucide-react";
import { HOME_COPY as C } from "@/data/homeContent";
import { useMediaActivity } from "./useMediaActivity";
import { homeImageKey, homeArtStyles } from "@/data/homeArtDirection";

export const HomeMedia = ({ name, eager = false, paused = false, showControls = true, className = "", sizes = "(max-width: 767px) 100vw, 60vw", children }) => {
  const { t } = useLang();
  const media = HOME_MEDIA[name];
  const frame = useRef(null);
  const player = useRef(null);
  const reduced = useReducedMotion();
  const { near, visible, foreground, mobile, saveData, breakpoint } = useMediaActivity(frame, eager);
  const imageKey = homeImageKey(media, breakpoint);
  const image = images[imageKey];
  const [userPaused, setUserPaused] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [supportsMp4] = useState(() => Boolean(document.createElement("video").canPlayType('video/mp4; codecs="avc1.42E01E"')));
  const mp4Src = mobile ? media.video?.mobile || media.video?.desktop : media.video?.desktop;
  const webmSrc = mobile ? media.video?.webmMobile || media.video?.webmDesktop : media.video?.webmDesktop;
  const videoSrc = supportsMp4 ? mp4Src : webmSrc || mp4Src;
  const hasVideo = Boolean(videoSrc);
  const srcSet = image?.files.map((f) => `${f.src} ${f.width}w`).join(", ");
  const source = image?.files.find((f) => f.width >= 960) || image?.files[image.files.length - 1];

  useEffect(() => { setFailed(false); setReady(false); }, [videoSrc]);
  useEffect(() => {
    const video = player.current;
    if (!video) return;
    if (visible && foreground && !reduced && !saveData && !paused && !userPaused) {
      video.play().catch((error) => { if (error.name === "NotAllowedError") setUserPaused(true); });
    } else video.pause();
  }, [visible, foreground, reduced, saveData, near, videoSrc, paused, userPaused]);

  return (
    <div ref={frame} className={`rh-media ${!image ? "rh-media-pending" : ""} ${className}`} data-testid={`home-media-${name}`} data-asset={imageKey || "pending"} data-breakpoint={breakpoint} style={homeArtStyles(media, imageKey)}>
      {eager && image && <link rel="preload" as="image" href={source.src} imageSrcSet={srcSet} imageSizes={sizes} fetchPriority="high" />}
      {image && <img data-testid={`home-image-${name}`} src={source.src} srcSet={srcSet} sizes={sizes} width={image.width} height={image.height} alt={t(media.alt)} loading={eager ? "eager" : "lazy"} fetchPriority={eager ? "high" : "auto"} decoding="async" />}
      {hasVideo && near && !reduced && !saveData && !failed && <video ref={player} key={videoSrc} data-testid={`home-video-${name}`} className={ready ? "is-ready" : ""} src={videoSrc} poster={source?.src} muted loop playsInline preload="none" controls={false} onPlaying={() => setReady(true)} onError={() => setFailed(true)} aria-label={t(media.alt)} />}
      {hasVideo && !reduced && !saveData && !failed && showControls && <button type="button" className="rh-motion-toggle" data-testid={`home-video-toggle-${name}`} aria-label={t(userPaused ? C.playFilm : C.pauseFilm)} title={t(userPaused ? C.playFilm : C.pauseFilm)} onClick={() => setUserPaused((value) => !value)}>{userPaused ? <Play size={15} /> : <Pause size={15} />}</button>}
      {children}
      {media.pending && (!hasVideo || failed) && <span className="rh-pending-label" data-testid={`home-pending-${name}`}>{t(media.pending)}</span>}
    </div>
  );
};